import type { KhoaDich } from '../../i18n';
import type {
  AiTokenGrantStatus,
  AiTokenRequestKind,
  AiTokenRequestStatus,
  CustomHostDnsError,
  CustomHostStatus,
  DeploymentMode,
  DomainVerifyMethod,
  PlanCode,
  ProvisioningStepStatus,
  TenantStatus,
  TenantTier,
} from './types';

type BienTheTrangThai = 'neutral' | 'primary' | 'success' | 'warning' | 'danger';

/**
 * Luật màu trạng thái:
 * **xanh = đang phục vụ · hổ phách = còn sống nhưng cần để mắt hoặc đang bị
 * chặn · xanh nhấn = đang chạy · xám = chưa phục vụ hoặc đã gỡ · đỏ = cần
 * người vào xem.**
 *
 * - TRIAL và ACTIVE cùng xanh: người dùng của cả hai đều vào được; nhãn đã phân
 *   biệt dùng thử với chính thức.
 * - GRACE_PERIOD hổ phách: vẫn phục vụ nhưng quá hạn thanh toán, sắp bị tạm ngưng.
 * - DRAFT xám chứ không đỏ: hồ sơ nháp là bình thường, không phải sự cố. Tenant
 *   trở về DRAFT vì triển khai thất bại thì màn chi tiết có dải báo riêng.
 *
 * Dùng biến thể của primitive `Badge`, nên công thức nền `-50` / chữ `-700` do
 * `Badge` lo; ở đây chỉ chọn ngữ nghĩa. Bảng mang **khoá dịch**, không mang
 * nhãn: màu là ngữ nghĩa của trạng thái, giống nhau ở mọi ngôn ngữ.
 */
export const TRANG_THAI_TENANT: Record<TenantStatus, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  DRAFT: { khoa: 'tenant.trangThai.DRAFT', variant: 'neutral' },
  PROVISIONING: { khoa: 'tenant.trangThai.PROVISIONING', variant: 'primary' },
  TRIAL: { khoa: 'tenant.trangThai.TRIAL', variant: 'success' },
  ACTIVE: { khoa: 'tenant.trangThai.ACTIVE', variant: 'success' },
  GRACE_PERIOD: { khoa: 'tenant.trangThai.GRACE_PERIOD', variant: 'warning' },
  SUSPENDED: { khoa: 'tenant.trangThai.SUSPENDED', variant: 'warning' },
  ARCHIVED: { khoa: 'tenant.trangThai.ARCHIVED', variant: 'neutral' },
  DELETED: { khoa: 'tenant.trangThai.DELETED', variant: 'neutral' },
};

/** Ba nhóm của ô thống kê ở màn danh sách. */
export const NHOM_DANG_PHUC_VU: readonly TenantStatus[] = ['TRIAL', 'ACTIVE', 'GRACE_PERIOD'];
export const NHOM_CHO_TRIEN_KHAI: readonly TenantStatus[] = ['DRAFT', 'PROVISIONING'];

/**
 * Nhãn của `plan_code`. Giao diện gọi là **hạn mức**, không gọi là gói dịch vụ:
 * gói dịch vụ là bảng gói của ADR-0019 ở màn Gói dịch vụ, một khái niệm khác.
 */
export const GOI_DICH_VU: Record<PlanCode, KhoaDich> = {
  TRIAL: 'tenant.goi.TRIAL',
  STANDARD: 'tenant.goi.STANDARD',
  PROFESSIONAL: 'tenant.goi.PROFESSIONAL',
  ENTERPRISE: 'tenant.goi.ENTERPRISE',
};

export const CACH_TRIEN_KHAI: Record<DeploymentMode, KhoaDich> = {
  SAAS: 'tenant.cachTrienKhai.SAAS',
  ON_PREMISE: 'tenant.cachTrienKhai.ON_PREMISE',
};

export const TANG: Record<TenantTier, KhoaDich> = {
  POOL: 'tenant.tang.POOL',
  DEDICATED: 'tenant.tang.DEDICATED',
  ISOLATED: 'tenant.tang.ISOLATED',
};

export const TRANG_THAI_BUOC: Record<
  ProvisioningStepStatus,
  { khoa: KhoaDich; variant: BienTheTrangThai }
> = {
  PENDING: { khoa: 'tenant.trangThaiBuoc.PENDING', variant: 'neutral' },
  RUNNING: { khoa: 'tenant.trangThaiBuoc.RUNNING', variant: 'primary' },
  SUCCEEDED: { khoa: 'tenant.trangThaiBuoc.SUCCEEDED', variant: 'success' },
  FAILED: { khoa: 'tenant.trangThaiBuoc.FAILED', variant: 'danger' },
};

export const CACH_XAC_MINH: Record<DomainVerifyMethod, KhoaDich> = {
  DNS_TXT: 'tenant.cachXacMinh.DNS_TXT',
  ADMIN_APPROVAL: 'tenant.cachXacMinh.ADMIN_APPROVAL',
};

/**
 * Tài nguyên trong hạn mức của gói. Danh mục mở (server còn thêm giá trị mới): giá trị lạ hiện nhãn chung
 * `TAI_NGUYEN_LA` kèm mã, không biến mất, không vỡ. Không có giới hạn request: băng thông không giới hạn (ADR-0087).
 * Không có số module cài được: bỏ ở ADR-0105. `bytes` thì định dạng dung lượng, còn lại là số đếm.
 */
export const TAI_NGUYEN: Record<string, { khoa: KhoaDich; bytes?: true }> = {
  MAX_USERS: { khoa: 'tenant.taiNguyen.MAX_USERS' },
  DB_BYTES: { khoa: 'tenant.taiNguyen.DB_BYTES', bytes: true },
  S3_BYTES: { khoa: 'tenant.taiNguyen.S3_BYTES', bytes: true },
  NOTIFY_MESSAGES_MONTH: { khoa: 'tenant.taiNguyen.NOTIFY_MESSAGES_MONTH' },
  AI_TOKENS_MONTH: { khoa: 'hanMucToken.tenTaiNguyen' },
};

/** Nhãn của tài nguyên chưa có trong `TAI_NGUYEN`; mã gốc vẫn hiện ngay dưới. */
export const TAI_NGUYEN_LA: { khoa: KhoaDich } = { khoa: 'tenant.taiNguyenLa' };

/**
 * Khoá tab "Gói & thuê bao" ở màn chi tiết tenant (gói dịch vụ, gói hạn mức, token AI), cũng là giá trị `?tab=` — form
 * tạo tenant dẫn thẳng tới tab này khi chưa gán được gói ban đầu, hàng chờ yêu cầu nạp token dẫn tới đây để xem mức dùng.
 */
export const TAB_GOI_THUE_BAO = 'goi-thue-bao';

/** Giá trị `?tab=` cũ của tab trên, trước khi gộp với các ô token AI — liên kết cũ vẫn mở đúng tab. */
export const TAB_GOI_THUE_BAO_CU = 'dich-vu-thue-bao';

/**
 * Tông của hạn mức token AI theo mức (ADR-0088): thanh tiến độ, nhãn trạng thái và dải báo.
 * Bình thường là màu nhấn, từ 80% là hổ phách (sắp hết), đã chặn là đỏ (cần người vào xem);
 * không giới hạn thì không có thanh. Thanh dùng bậc `-500` vì chỉ cần tương phản 3:1.
 */
export const TONG_TOKEN_AI: Record<
  'binh-thuong' | 'canh-bao' | 'da-chan',
  { thanh: string; chu: string; badge: BienTheTrangThai | null; khoaBadge: KhoaDich | null }
> = {
  'binh-thuong': { thanh: 'bg-primary-500', chu: 'text-content-primary', badge: null, khoaBadge: null },
  'canh-bao': {
    thanh: 'bg-warning-500',
    chu: 'text-warning-700',
    badge: 'warning',
    khoaBadge: 'hanMucToken.trangThai.canhBao',
  },
  'da-chan': { thanh: 'bg-danger-500', chu: 'text-danger-700', badge: 'danger', khoaBadge: 'hanMucToken.trangThai.daChan' },
};

type BienThePhanLoai = 'violet' | 'teal' | 'orange' | 'info';

/**
 * Trạng thái dòng token cấp thêm (ADR-0091 mục 2): xanh = đang cộng vào hạn mức, xanh nhấn = chờ tới ngày,
 * xám = không còn tác dụng (hết hạn hoặc đã thu hồi). Thu hồi xám chứ không đỏ: là quyết định đã làm, không
 * phải sự cố cần người vào xem.
 */
export const TRANG_THAI_CAP_THEM: Record<AiTokenGrantStatus, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  ACTIVE: { khoa: 'tokenAi.trangThaiCapThem.ACTIVE', variant: 'success' },
  SCHEDULED: { khoa: 'tokenAi.trangThaiCapThem.SCHEDULED', variant: 'primary' },
  EXPIRED: { khoa: 'tokenAi.trangThaiCapThem.EXPIRED', variant: 'neutral' },
  REVOKED: { khoa: 'tokenAi.trangThaiCapThem.REVOKED', variant: 'neutral' },
};

/** Loại cấp thêm và loại yêu cầu là **phân loại**, không phải mức độ: dùng thang phân loại của `Badge`. */
export const LOAI_YEU_CAU_TOKEN: Record<AiTokenRequestKind, { khoa: KhoaDich; variant: BienThePhanLoai }> = {
  NAP_THEM: { khoa: 'tokenAi.loai.NAP_THEM', variant: 'teal' },
  GOI_TAM_THOI: { khoa: 'tokenAi.loai.GOI_TAM_THOI', variant: 'violet' },
  GIA_HAN: { khoa: 'tokenAi.loai.GIA_HAN', variant: 'orange' },
  NAP_TOKEN: { khoa: 'banToken.yeuCau.loaiNapToken', variant: 'info' },
};

/**
 * Trạng thái yêu cầu (ADR-0091 mục 3): hổ phách = chờ một người xử lý, xanh = đã được cấp, đỏ = bị từ chối
 * (người gửi cần đọc lý do), xám = người gửi tự huỷ.
 */
export const TRANG_THAI_YEU_CAU_TOKEN: Record<AiTokenRequestStatus, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  PENDING: { khoa: 'tokenAi.trangThaiYeuCau.PENDING', variant: 'warning' },
  APPROVED: { khoa: 'tokenAi.trangThaiYeuCau.APPROVED', variant: 'success' },
  REJECTED: { khoa: 'tokenAi.trangThaiYeuCau.REJECTED', variant: 'danger' },
  CANCELLED: { khoa: 'tokenAi.trangThaiYeuCau.CANCELLED', variant: 'neutral' },
};

/**
 * Domain thương hiệu (ADR-0097), cùng luật màu với trạng thái tenant: **xanh = đang phục vụ · xanh nhấn = đã đạt,
 * chờ người bấm · hổ phách = chờ khách hoặc đang chặn · xám = đã gỡ**. `CHO_DNS` hổ phách vì việc nằm ở phía khách
 * (tạo bản ghi DNS), không phải sự cố.
 */
export const TRANG_THAI_DIA_CHI_RIENG: Record<CustomHostStatus, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  CHO_DNS: { khoa: 'diaChiRieng.trangThai.CHO_DNS', variant: 'warning' },
  DA_XAC_MINH: { khoa: 'diaChiRieng.trangThai.DA_XAC_MINH', variant: 'primary' },
  DANG_DUNG: { khoa: 'diaChiRieng.trangThai.DANG_DUNG', variant: 'success' },
  TAM_NGUNG: { khoa: 'diaChiRieng.trangThai.TAM_NGUNG', variant: 'warning' },
  DA_GO: { khoa: 'diaChiRieng.trangThai.DA_GO', variant: 'neutral' },
};

/** Lý do DNS chưa đạt → câu nói cách sửa. */
export const LOI_DNS_DIA_CHI_RIENG: Record<CustomHostDnsError, KhoaDich> = {
  TXT_MISSING: 'diaChiRieng.loiDns.TXT_MISSING',
  TXT_MISMATCH: 'diaChiRieng.loiDns.TXT_MISMATCH',
  TARGET_MISMATCH: 'diaChiRieng.loiDns.TARGET_MISMATCH',
  DNS_UNAVAILABLE: 'diaChiRieng.loiDns.DNS_UNAVAILABLE',
};
